import { FC } from 'react';
import cn from 'classnames';

import LocalLoader from 'src/shared/ui/LocalLoader/LocalLoader';
import { PaginationType } from 'constants/enums';
import styles from 'components/Sections/PsychicsSetMediumSizeImagesClone/PsychicsSetMediumSizeImages.module.scss';
import type { PsychicsSetMediumSizeImagesCloneButtons as ButtonsInterface, IPaginationActions } from 'components/Sections/PsychicsSetMediumSizeImagesClone/declarations';
import { DataButton, DataLink } from 'components/Shared/SharedComponents';
import { useButtonBgImageStyles } from 'lib/shared.hook';
import { capitalizeFirstLetter } from 'lib/text.service';

/* Nested component */
const Buttons: FC<ButtonsInterface> = ({ primary, secondary, buttonPosition }) => {
  const bgImageStyles = useButtonBgImageStyles(primary!);

  if (!primary && !secondary) {
    return null;
  }

  if (primary && secondary) {
    const commonClasses = cn(styles.psychicButton, styles.psychicButtonMobileSmall);

    return (
      <div className={cn(styles.psychicButtonWrapper, styles.psychicButtonMargin)}>
        <DataLink
          link={primary}
          href={primary.src!}
          className={cn(commonClasses, styles.psychicButtonPrimary)}
        >
          {primary.title}
        </DataLink>
        <DataLink
          link={secondary}
          href={secondary.src!}
          className={cn(commonClasses, styles.psychicButtonSecondary)}
        >
          {secondary.title}
        </DataLink>
      </div>
    );
  }

  const link = primary || secondary!;

  return (
    <DataLink
      link={link}
      href={link.src!}
      style={bgImageStyles || {}}
      className={cn(
        styles.psychicButton,
        styles.psychicButtonMargin,
        {
          [styles.psychicButtonPrimary]: Boolean(primary),
          [styles.psychicButtonSecondary]: Boolean(secondary),
          [styles.buttonWithBg]: Boolean(bgImageStyles),
        },
        styles[`buttonPosition${capitalizeFirstLetter(buttonPosition)}`],
      )}
    >
      {link.title}
    </DataLink>
  );
};

/* Main Component */
const PaginationActions: FC<IPaginationActions> = ({
  link,
  paginationType,
  loadingScroll,
  psychics,
  paginationButton,
  loadMorePsychicHandler,
  isShowLoadMoreButton,
  observerRef,
  hasNextPage,
  secondaryButton,
  buttonPosition,
}) => {
  if (paginationType === PaginationType.SCROLL) {
    if (!hasNextPage) {
      return null;
    }

    const shouldShowLoading = loadingScroll && psychics?.length > 0;

    return (
      <>
        {shouldShowLoading && <LocalLoader />}
        {paginationButton && (
          <DataButton
            ref={observerRef}
            className={styles.loadButton}
            onClick={loadMorePsychicHandler}
            style={isShowLoadMoreButton
              ? {}
              : { visibility: 'hidden', height: 0 }}
          >
            {paginationButton.title}
          </DataButton>
        )}
      </>
    );
  }

  return (
    <Buttons
      primary={link!}
      secondary={secondaryButton}
      buttonPosition={buttonPosition}
    />
  );
};

export default PaginationActions;
